/**
 * Jurisdiction-specific affidavit requirements, ported from the nationwide
 * research AG (Antigravity/Gemini) built in a separate lab repo
 * (profenz-gemini-lab-v1/research/jurisdictions/us_affidavit_jurisdiction_codex.json,
 * 2026-09-11). Only jurisdictions ProFenz actually has fixture/pilot data for
 * are ported here -- deliberately not all 52; see the Carolina-pilot-first
 * scoping decision. `getAffidavitJurisdictionRule()` returns undefined for
 * anything not yet ported, which callers must treat as "no jurisdiction
 * guidance available" rather than assuming a default.
 *
 * This does not gate affidavit creation -- the review/edit step in Mission
 * Control (company review before client delivery) is where a human corrects
 * anything, per Russell's review model. This module exists to surface the
 * right context and required fields at that review step, not to block it.
 */
export type AffidavitJurisdictionRule = {
  stateCode: string;
  stateName: string;
  primaryDocumentTitle: string;
  requiresNotary: boolean;
  militaryStatusRequired: boolean;
  governingRules: string;
  specialProceduralNote?: string;
};

const AFFIDAVIT_JURISDICTION_RULES: Record<string, AffidavitJurisdictionRule> = {
  NC: {
    stateCode: 'NC',
    stateName: 'North Carolina',
    primaryDocumentTitle: 'Affidavit of Service',
    requiresNotary: true,
    militaryStatusRequired: true,
    governingRules: 'N.C. Gen. Stat. § 1-75.10, N.C. Rule 4(h1)',
    specialProceduralNote: 'NC Rule 4(h1) requires an initial unexecuted return by a proper officer (Sheriff) before private server authority triggers.',
  },
  SC: {
    stateCode: 'SC',
    stateName: 'South Carolina',
    primaryDocumentTitle: 'Affidavit of Service',
    requiresNotary: true,
    militaryStatusRequired: false,
    governingRules: 'S.C. R. Civ. P. 4(g), S.C. Code § 23-19-10',
    specialProceduralNote: 'Must include statutory service fee itemization ($15 initial + $5/attempt, max 2 attempts = $25 max).',
  },
};

export function getAffidavitJurisdictionRule(stateCode: string | undefined): AffidavitJurisdictionRule | undefined {
  if (!stateCode) return undefined;
  return AFFIDAVIT_JURISDICTION_RULES[stateCode.toUpperCase()];
}
